import {EquityOrderType, EquityWizardData} from "../../models/EquityWizardData";
import {
    EquityWizardDataAdviceChange,
    EquityWizardDataChange,
    EquityWizardDataExpiryChange,
    EquityWizardDataInstructionChange,
    EquityWizardDataLifetimeChange,
    EquityWizardDataLimitChange,
    EquityWizardDataPortfolioChange,
    EquityWizardDataQuantityChange,
    EquityWizardDataQuoteChange,
    EquityWizardDataSecurityChange,
    EquityWizardDataSideChange,
    EquityWizardDataStopLossLimitChange,
    EquityWizardDataTotalValueChange,
    EquityWizardDataTypeChange,
    EquityWizardNameChange,
    EquityWizardAddChildOrderChange,
    EquityWizardIsParentOrderChange,
    isAdviceChange,
    addChildOrderChange,
    isExpiryChange,
    isInstructionChange,
    isLifetimeChange,
    isLimitChange,
    isLimitToMidChange,
    isNameChange,
    isOrderTypeChange,
    isParentOrderChange,
    isPortfolioChange,
    isQuantityChange,
    isQuoteChange,
    isSecurityChange,
    isSideChange,
    isStopLossLimitChange,
    isStopLossToggleChange,
    isTotalValueChange,
    removeChildOrderChange,
    EquityWizardRemoveChildOrderChange,
    updateChildOrderChange,
    EquityWizardUpdateChildOrderChange
} from "../../models/EquityWizardDataChange";
import {OrderLifetime, OrderSide} from "../../../../../../orders/models/OrderRecord";
import {Exchange} from "../../../../../../instruments/exchange/Exchange";

export function processUpdate(current: EquityWizardData, change: EquityWizardDataChange): EquityWizardData {
    if (isSecurityChange(change)) {
        return processSecurityChange(current, change);
    } else if (isNameChange(change)) {
        return processNameChange(current, change);
    } else if (isSideChange(change)) {
        return processSideChange(current, change);
    } else if (isQuantityChange(change)) {
        return processQuantityChange(current, change);
    } else if (isLimitChange(change)) {
        return processLimitChange(current, change);
    } else if (isTotalValueChange(change)) {
        return processTotalValueChange(current, change);
    } else if (isLifetimeChange(change)) {
        return processLifetimeChange(current, change);
    } else if (isExpiryChange(change)) {
        return processExpiryChange(current, change);
    } else if (isInstructionChange(change)) {
        return processInstructionChange(current, change);
    } else if (isOrderTypeChange(change)) {
        return processOrderTypeChange(current, change);
    } else if (isAdviceChange(change)) {
        return processAdviceChange(current, change);
    } else if (isQuoteChange(change)) {
        return processQuoteChange(current, change);
    } else if (isPortfolioChange(change)) {
        return processPortfolioChange(current, change);
    } else if (isStopLossToggleChange(change)) {
        return processStopLossToggle(current);
    } else if (isStopLossLimitChange(change)) {
        return processStopLossLimitChange(current, change);
    } else if (isLimitToMidChange(change)) {
        return processLimitToMidChange(current);
    } else if (isParentOrderChange(change)) {
        return processIsParentOrderChange(current, change);
    } else if (addChildOrderChange(change)) {
        return processAddChildOrderChange(current, change);
    } else if (removeChildOrderChange(change)) {
        return processRemoveChildOrderChange(current, change);
    } else if (updateChildOrderChange(change)) {
        return processUpdateChildOrderChange(current, change);
    } else {
        console.warn("Unhandled change type", change);
        return current;
    }
}

/* Reset the limit and quote so we can set the correct values again once we get the quote back */
function processSecurityChange(current: EquityWizardData, change: EquityWizardDataSecurityChange): EquityWizardData {
    return {
        ...current,
        code: change.security.code,
        exchange: change.security.exchange,
        strategy: change.strategy,
        limit: undefined,
        quote: undefined
    };
}

function processNameChange(current: EquityWizardData, change: EquityWizardNameChange): EquityWizardData {
    const updated: EquityWizardData = {
        ...current,
        name: change.name,
    };
    return updated;
}

function processSideChange(current: EquityWizardData, change: EquityWizardDataSideChange): EquityWizardData {
    let updated: EquityWizardData = {
        ...current,
        side: change.side,
        stopLossLimit: undefined,
        isStopLoss: false
    };
    updated = updateLimit(updated);
    return updateTotalValue(updated);
}

function processLifetimeChange(current: EquityWizardData, change: EquityWizardDataLifetimeChange): EquityWizardData {
    let lifetime: OrderLifetime | undefined = change.lifetime;
    if (current.exchange && current.exchange === Exchange.AXW) {
        lifetime = OrderLifetime.END_OF_DAY;
    }
    return {
        ...current,
        lifetime
    };
}

function processQuantityChange(current: EquityWizardData, change: EquityWizardDataQuantityChange): EquityWizardData {
    const updated: EquityWizardData = {
        ...current,
        quantity: change.quantity
    };
    return updateTotalValue(updated);
}

function processLimitChange(current: EquityWizardData, change: EquityWizardDataLimitChange): EquityWizardData {
    const updated: EquityWizardData = {
        ...current,
        limit: change.limit
    };

    switch (current.type) {
        case EquityOrderType.QUANTITY:
            return updateTotalValue(updated);
        case EquityOrderType.TOTAL_VALUE:
            return updateQuantity(updated);
    }
}

function processTotalValueChange(current: EquityWizardData,
                                 change: EquityWizardDataTotalValueChange): EquityWizardData {
    const updated: EquityWizardData = {
        ...current,
        totalValue: change.totalValue
    };

    return updateQuantity(updated);
}

function processExpiryChange(current: EquityWizardData, change: EquityWizardDataExpiryChange): EquityWizardData {
    return {
        ...current,
        expiry: change.expiry ?? undefined
    };
}

function processInstructionChange(current: EquityWizardData,
                                  change: EquityWizardDataInstructionChange): EquityWizardData {
    let updated: EquityWizardData = {
        ...current,
        pricingInstruction: change.instruction
    };
    if (current.pricingInstruction !== change.instruction) {
        updated = updateLimit(updated);
        return updateTotalValue(updated);
    }
    return updated;
}

function processOrderTypeChange(current: EquityWizardData, change: EquityWizardDataTypeChange): EquityWizardData {
    return {
        ...current,
        type: change.orderType
    };
}

function processAdviceChange(current: EquityWizardData, change: EquityWizardDataAdviceChange): EquityWizardData {
    return {
        ...current,
        advice: change.advice ?? undefined
    };
}

function processQuoteChange(current: EquityWizardData, change: EquityWizardDataQuoteChange): EquityWizardData {
    // We could end up with quotes out of sync with the current security, and therefore want to skip those
    if ((current.code !== change.quote.code) || (current.exchange !== change.quote.exchange)) {
        return current;
    }
    let updated: EquityWizardData = {
        ...current,
        quote: change.quote
    };
    if (current.limit == null) {
        updated = updateLimit(updated);
        return updateTotalValue(updated);
    }
    return updated;
}

function processPortfolioChange(current: EquityWizardData, change: EquityWizardDataPortfolioChange): EquityWizardData {
    return {
        ...current,
        portfolio: change.portfolio
    };
}

/* Call this after updating the current state to apply any required logic for updating the limit of an order */
function updateLimit(current: EquityWizardData): EquityWizardData {
    if ((current.quote == null) || (current.side == null)) {
        return current;
    }
    const limit = current.side === OrderSide.BUY ? current.quote.bid : current.quote.ask;
    if (limit == null) {
        return current;
    }

    return {
        ...current,
        limit
    };
}

function updateTotalValue(current: EquityWizardData): EquityWizardData {
    if (current.limit == null) {
        return {
            ...current,
            totalValue: 0
        };
    } else {
        const quantity = current.quantity ?? 0;
        return {
            ...current,
            totalValue: quantity * current.limit
        };
    }
}

function updateQuantity(current: EquityWizardData): EquityWizardData {

    if (current.limit == null || current.totalValue == null) {
        return {
            ...current,
            quantity: 0
        };
    } else {
        const quantity = current.limit !== 0 ? Math.floor(current.totalValue / current.limit) : 0;
        return {
            ...current,
            quantity
        };
    }
}

function processStopLossToggle(current: EquityWizardData): EquityWizardData {
    const isStopLoss = !current.isStopLoss;
    const stopLossLimit = isStopLoss ? current.limit : null;

    return {
        ...current,
        isStopLoss,
        stopLossLimit
    };
}

function processStopLossLimitChange(current: EquityWizardData,
                                    change: EquityWizardDataStopLossLimitChange): EquityWizardData {
    return {
        ...current,
        stopLossLimit: change.stopLossLimit
    };
}

function processLimitToMidChange(current: EquityWizardData): EquityWizardData {
    if (current.quote == null) {
        return current;
    }
    return {
        ...current,
        limit: current.quote.mid
    };
}

function processIsParentOrderChange(current: EquityWizardData,
                                    change: EquityWizardIsParentOrderChange): EquityWizardData {
    return {
        ...current,
        isParentOrder: change.isParentOrder
    };
}

function processAddChildOrderChange(current: EquityWizardData, change: EquityWizardAddChildOrderChange): EquityWizardData {
    return {
        ...current,
        childOrders: [...current.childOrders, change.childOrder]
    };
}

function processRemoveChildOrderChange(current: EquityWizardData, change: EquityWizardRemoveChildOrderChange): EquityWizardData {
    return {
        ...current,
        childOrders: [...current.childOrders.filter((item, index) => index !== change.index)]
    };
}

function processUpdateChildOrderChange(current: EquityWizardData, change: EquityWizardUpdateChildOrderChange): EquityWizardData {
    return {
        ...current,
        childOrders: [...current.childOrders.map((item, index) => index === change.index ? change.childOrder : item)]
    };
}
